// =============================================================
// LANDING PAGE — EDITABLE CONTENT
// =============================================================
// Single source of truth for anything you might want to change on
// the public landing page without touching layout or component code.
// Update the values below and the site reflects them on next reload.
//
// - Do NOT introduce a CMS here.
// - Do NOT couple this file to backend/APIs/quiz/results.
// - The primary hero image URL is also referenced from
//   /app/frontend/public/index.html as a <link rel="preload"> so the
//   correct image renders immediately on the first paint (no flash,
//   no swap of an older asset). If you change HERO.image_url here you
//   should also update the preload URL in index.html for perfect parity.
// =============================================================

// ─────────────────────────────────────────────────────────────
// ✏️  HERO — EDIT HERE
// ─────────────────────────────────────────────────────────────
// This is the ONLY place to change the hero section of the landing page.
// You do NOT need to touch any component, layout, or design code.
// Just edit the values below and reload the site.
//
// What you can edit safely:
//   1) image_url  → the big hero image (English + Italian share it)
//   2) headline   → the big title ("Your Hair. Understood.")
//   3) sub        → the supporting text under the title
//   4) cta        → the button text ("Start the Assessment")
//   5) eyebrow    → the small ALL-CAPS lines above the title
//   6) trust      → the tiny reassurance chips below the CTA
//
// Language rule: every text field has both `en` (English) and `it` (Italian).
// Edit BOTH so users in either language see your new copy.
// ─────────────────────────────────────────────────────────────
export const HERO = {
  // ✏️ EDIT: HERO IMAGE
  // Replace this URL to change the big hero image on the landing page.
  // Tip: if you swap this URL, also update the <link rel="preload"> in
  // /app/frontend/public/index.html to keep the first-paint clean.
  image_url:
    "https://images.unsplash.com/photo-1560869713-7d0954430ff2?w=1600&q=80&auto=format&fit=crop",

  // ✏️ EDIT: EYEBROW (small ALL-CAPS lines above the headline)
  // Each string in the array is rendered on its own line.
  eyebrow: {
    en: ["A PERSONALIZED", "COSMETIC HAIR ASSESSMENT"],
    it: ["UN'ANALISI COSMETICA", "PERSONALIZZATA DEI CAPELLI"],
  },

  // ═══════════════════════════════════════════════════════════
  // ✏️  EDIT HERE — HERO HEADLINE  ("Your Hair. Understood.")
  // ═══════════════════════════════════════════════════════════
  // This is the BIG TITLE on the landing page.
  // To change "Your Hair. Understood." → edit the `en` line below.
  // To change the Italian version      → edit the `it` line below.
  headline: {
    en: "Your Hair. Understood.",           // ← EDIT: English headline (replace between the quotes)
    it: "I tuoi capelli. Compresi.",        // ← EDIT: Italian headline (replace between the quotes)
  },

  // ✏️ EDIT: HERO SUPPORTING TEXT (the sentence under the headline)
  sub: {
    en: "Take the 5-minute assessment — free preview, no account needed.",
    it: "Fai l'analisi di 5 minuti — anteprima gratuita, senza registrazione.",
  },

  // ✏️ EDIT: CTA BUTTON TEXT (the main button)
  cta: {
    en: "Start the Assessment",
    it: "Inizia l'analisi",
  },

  // ✏️ EDIT: TRUST CHIPS (small reassurance items below the CTA)
  trust: {
    en: ["5 minutes", "Free preview", "No account required"],
    it: ["5 minuti", "Anteprima gratuita", "Nessun account richiesto"],
  },
};

// ═══════════════════════════════════════════════════════════════
// ✏️  REVIEWS / TESTIMONIALS — EDIT HERE
// ═══════════════════════════════════════════════════════════════
// Small placeholder testimonials shown near the top of the landing page.
// Replace the `quote`, `name` and `city` values below with real customer
// feedback whenever you have it — nothing else in the page needs to change.
//
// Each testimonial has EN + IT. If you don't have a translation yet, leave
// `it` empty for that entry — the site will fall back to the English text.
//
// Field guide:
//   - kicker    → small ALL-CAPS label above the section headline
//   - headline  → the section headline
//   - items[].quote → the review text (short, one sentence works best)
//   - items[].name  → first name only (no last names, no fake IDs)
//   - items[].city  → optional location context
// ═══════════════════════════════════════════════════════════════
export const REVIEWS = {
  kicker: { en: "WHAT OUR COMMUNITY SAYS", it: "COSA DICE LA NOSTRA COMMUNITY" },
  headline: {
    en: "Real clarity, in five minutes.",
    it: "Chiarezza vera, in cinque minuti.",
  },
  items: [
    {
      quote: {
        en: "After the assessment I finally understood what my hair actually needs. I stopped guessing which shampoo to buy and built a routine that made sense for me.",
        it: "Dopo l'analisi ho finalmente capito di cosa hanno bisogno i miei capelli. Ho smesso di tirare a indovinare quale shampoo comprare e ho costruito una routine sensata per me.",
      },
      name: { en: "Sofia", it: "Sofia" },
      city: { en: "Milano", it: "Milano" },
    },
    {
      quote: {
        en: "This test completely changed the way I look at my hair. I finally understood my hair and scalp needs and knew what kind of products I should actually be using.",
        it: "Questo test ha cambiato completamente il modo in cui guardo i miei capelli. Ho capito davvero cosa serve al mio cuoio capelluto e quali prodotti usare.",
      },
      name: { en: "Giulia", it: "Giulia" },
      city: { en: "Rome", it: "Roma" },
    },
    {
      quote: {
        en: "Choosing hair products used to feel impossible. The KT Milano assessment made it clear which ingredients my curls actually respond to.",
        it: "Scegliere i prodotti per capelli sembrava impossibile. L'analisi KT Milano ha reso chiaro a quali ingredienti i miei ricci rispondono davvero.",
      },
      name: { en: "Chiara", it: "Chiara" },
      city: { en: "Turin", it: "Torino" },
    },
  ],
};


export const HOW_IT_WORKS = {
  kicker: { en: "How it works", it: "Come funziona" },
  steps: [
    {
      icon: "file",
      title: {
        en: "Answer a few simple questions.",
        it: "Rispondi a poche semplici domande.",
      },
    },
    {
      icon: "sliders",
      title: {
        en: "Get your personalized hair analysis.",
        it: "Ricevi la tua analisi personalizzata.",
      },
    },
    {
      icon: "lock",
      title: {
        en: "Receive a tailored routine.",
        it: "Scopri la tua routine su misura.",
      },
    },
  ],
};

// ─────────────────────────────────────────────────────────────
// ✏️  SCIENCE-BACKED CARE — EDIT HERE
// ─────────────────────────────────────────────────────────────
// This controls the "SCIENCE-BACKED CARE" section further down the
// landing page (the large image + headline + supporting text + CTA).
// Edit the values below and reload — no component/layout code to touch.
//
// What you can edit safely:
//   1) image_url  → the LARGE IMAGE in the SCIENCE-BACKED CARE section
//   2) kicker     → the small ALL-CAPS label above the headline
//   3) headline   → the section title
//   4) sub        → the supporting sentence under the headline
//   (The CTA button re-uses HERO.cta on purpose — same button copy.)
//
// Language rule: every text field has both `en` (English) and `it` (Italian).
// Edit BOTH so users in either language see your new copy.
// ─────────────────────────────────────────────────────────────
export const SCIENCE = {
  // ✏️ EDIT: SCIENCE-BACKED CARE IMAGE  (a.k.a. SCIENCE_BACKED_IMAGE)
  // Replace this URL to change the LARGE image in the SCIENCE-BACKED CARE
  // section on the landing page. Nothing else needs to move.
  image_url:
    "https://images.unsplash.com/photo-1595351298020-038700609878?w=1400&q=80&auto=format&fit=crop",

  // ✏️ EDIT: KICKER (small ALL-CAPS label above the section headline)
  kicker: { en: "SCIENCE-BACKED CARE", it: "SCIENZA E CURA" },

  // ✏️ EDIT: SECTION HEADLINE
  headline: {
    en: "Better hair decisions start with real insights.",
    it: "Decisioni migliori per i tuoi capelli iniziano da veri insight.",
  },

  // ✏️ EDIT: SUPPORTING TEXT under the headline
  sub: {
    en: "Understand what your hair really needs and get a personalized routine, powered by KT Milano.",
    it: "Capisci cosa serve davvero ai tuoi capelli e ricevi una routine personalizzata, firmata KT Milano.",
  },

  // The button on this section intentionally re-uses the Hero CTA text
  // so both CTAs stay consistent. Edit HERO.cta above to change this too.
  cta: HERO.cta,
};

// ═══════════════════════════════════════════════════════════════
// ✏️  EDIT HERE — AGE-QUESTION IMAGES (WOMEN & MEN, KEPT SEPARATE)
// ═══════════════════════════════════════════════════════════════
// The quiz still uses the exact same age bands, order, labels, scoring
// and logic (see /src/data/assessment.js → AGE_QUESTION). Only the
// image shown for each option changes based on the selected gender.
//
// STRICT SEPARATION — Women and Men image sets are 100% disjoint.
// No URL is ever shared between AGE_IMAGES.woman and AGE_IMAGES.man,
// and neither set collides with the female data file (data/assessment.js)
// or the male data file (data/male_assessment.js) quiz thumbnails.
//
// HOW TO REPLACE AN IMAGE:
//   1. Find the exact line below (each line is one specific image).
//   2. Replace ONLY the URL between the quotes.
//   3. Save the file. That's it — no other code needs to change.
//   4. Changing a WOMAN url does NOT affect any MAN url, and vice versa.
// ═══════════════════════════════════════════════════════════════
export const AGE_IMAGES = {
  // ─────────────────────────────────────────────────────────────
  // ✏️  WOMEN — 4 age images (each independently editable)
  // ─────────────────────────────────────────────────────────────
  woman: {
    // ✏️ EDIT: WOMAN → Under 18
    under_18: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=800&q=80&auto=format&fit=crop",
    // ✏️ EDIT: WOMAN → 18 – 34
    "18_34":  "https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?w=800&q=80&auto=format&fit=crop",
    // ✏️ EDIT: WOMAN → 35 – 54
    "35_54":  "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=800&q=80&auto=format&fit=crop",
    // ✏️ EDIT: WOMAN → 55 +
    "55_plus":"https://images.unsplash.com/photo-1573497019418-b400bb3ab074?w=800&q=80&auto=format&fit=crop",
  },

  // ─────────────────────────────────────────────────────────────
  // ✏️  MEN — 4 age images (each independently editable)
  // Must stay 100% separate from the WOMEN block above.
  // ─────────────────────────────────────────────────────────────
  man: {
    // ✏️ EDIT: MAN → Under 18
    under_18: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=800&q=80&auto=format&fit=crop",
    // ✏️ EDIT: MAN → 18 – 34
    // (Was 1523251343397 — collided with the female data file's IMG.man; swapped for a dedicated male portrait.)
    "18_34":  "https://images.unsplash.com/photo-1552058544-f2b08422138a?w=800&q=80&auto=format&fit=crop",
    // ✏️ EDIT: MAN → 35 – 54
    "35_54":  "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=800&q=80&auto=format&fit=crop",
    // ✏️ EDIT: MAN → 55 +
    "55_plus":"https://images.unsplash.com/photo-1540569014015-19a7be504e3a?w=800&q=80&auto=format&fit=crop",
  },
};

/**
 * Given the raw AGE_QUESTION definition + a selected gender, return a
 * copy whose options carry gender-appropriate images. Only image mapping
 * changes — question text, option order, IDs, and scoring are untouched.
 *
 * Each option is also tagged with a gender-scoped overrideKey so that
 * admin image overrides never leak across genders — an admin uploading
 * a picture for `q_age_band_woman_under_18` will only affect women, and
 * uploading `q_age_band_man_under_18` only affects men. Legacy (gender-
 * agnostic) `q_age_band_*` overrides are intentionally NOT resolved so
 * women and men image sets stay 100% disjoint at runtime.
 */
export function ageQuestionForGender(baseQuestion, gender) {
  const g = gender === "man" ? "man" : "woman";
  const map = AGE_IMAGES[g];
  return {
    ...baseQuestion,
    options: baseQuestion.options.map((opt) => ({
      ...opt,
      image: map[opt.id] || opt.image,
      overrideKey: `q_age_band_${g}_${opt.id}`,
    })),
  };
}
